import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { Call } from '@gem/call/CallStore/Call';
import {
  ICallPersonalizedPermissions,
  PermissionsPersonalizedValue,
} from '@gem/call/Model/callPermissions/callPermissionsModel.types';
import { PERMISSIONS_CUSTOM_TRANSLATIONS } from '@gem/call/constants';
import { useTranslation } from '@gem/modules/i18n';
import { ICommonPopupsProps } from '@gem/popups';

import { ICallChangeMemberPermissionPopupProps } from './CallChangeMemberPermissionPopup.types';

interface IPermissionValuesOption {
  key: PermissionsPersonalizedValue;
  title: string;
  isActive: boolean;
  onChange(): void;
}

interface ICallChangeMemberPermissionPopupPresenter {
  ongoingCall: Call;
  isNewMemberPermissions: boolean;
  editedPermissions: ICallPersonalizedPermissions | null;
  permissions: ICallPersonalizedPermissions | null;
  channelId: string | null;
  permissionValues: IPermissionValuesOption[];
  onClose(): void;
  goBack(): void;
  setValue(value: PermissionsPersonalizedValue): void;
}

export const useCallChangeMemberPermissionPopupPresenter = (
  props: ICommonPopupsProps<ICallChangeMemberPermissionPopupProps>
): ICallChangeMemberPermissionPopupPresenter => {
  const { t } = useTranslation();
  const { call, callController, memberPermissionsId, permissionType } = props.params!;

  const presenter = useLocalObservable<ICallChangeMemberPermissionPopupPresenter>(() => ({
    onClose(): void {
      props.popupInstance.close();
      props.onClose?.();
    },

    goBack(): void {
      props.popupInstance.close();
      props.onBack?.();
    },

    setValue(value: PermissionsPersonalizedValue): void {
      if (value) {
        presenter.ongoingCall.permissions.changeEditedPermission(
          memberPermissionsId,
          permissionType,
          value
        );
      }
    },

    get ongoingCall(): Call {
      return call;
    },

    get isNewMemberPermissions(): boolean {
      return !presenter.ongoingCall?.permissions.customPermissions.has(memberPermissionsId);
    },

    get editedPermissions(): ICallPersonalizedPermissions | null {
      return presenter.ongoingCall.permissions.editedPermissions?.permissions || null;
    },

    get permissions(): ICallPersonalizedPermissions | null {
      const personalizedPermissions = presenter.editedPermissions;

      if (!personalizedPermissions) {
        return null;
      }

      return (
        presenter.ongoingCall.permissions.getMemberPersonalizedPermissions(
          personalizedPermissions
        ) || null
      );
    },

    get channelId(): string | null {
      return presenter.ongoingCall.channelId || null;
    },

    get permissionValues(): IPermissionValuesOption[] {
      if (!presenter.permissions) {
        return [];
      }

      return Object.values(PermissionsPersonalizedValue).map(
        (item: PermissionsPersonalizedValue) => ({
          key: item,
          title: t(PERMISSIONS_CUSTOM_TRANSLATIONS[item]),
          isActive:
            presenter.permissions && permissionType
              ? item === presenter.permissions[permissionType]
              : false,
          onChange: (): void => presenter.setValue(item),
        })
      );
    },
  }));

  useEffect(() => {
    if (!presenter.ongoingCall.isStarted) {
      presenter.onClose();
    }
  }, [presenter.ongoingCall.isStarted]);

  useEffect(() => {
    return (): void => {
      if (!presenter.isNewMemberPermissions && presenter.channelId && presenter.editedPermissions) {
        callController.setCallPermissionsForUser(
          presenter.channelId,
          memberPermissionsId,
          presenter.editedPermissions
        );
      }
    };
  }, []);

  return presenter;
};
